import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle, Gift, Calendar, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { NavbarDemo } from "./NavBar";
import { BACKEND_BASE_URL } from "../config/api.config";

/**
 * Thank You Page for Holiday Gift Card Purchase
 *
 * Shows after successful payment
 * Includes CTAs for:
 * - Buy another gift card
 * - Book a massage
 *
 * Also verifies payment and processes if backend wasn't notified
 * (Alternative to webhooks)
 */
export default function HolidayGiftCardThankYou() {
  const navigate = useNavigate();
  const location = useLocation();
  const [verifying, setVerifying] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);

  // Get payment details from location state (if available)
  const paymentData = location.state?.paymentData || {};
  const paymentIntentId =
    paymentData?.paymentIntentId ||
    new URLSearchParams(location.search).get("payment_intent");

  // Verify payment when page loads (alternative to webhooks)
  useEffect(() => {
    const verifyAndProcessPayment = async () => {
      if (!paymentIntentId) {
        setVerifying(false);
        return;
      }

      try {
        // Verify payment status
        const verifyResponse = await fetch(
          `${BACKEND_BASE_URL}/api/gift-cards/holiday/verify-payment?paymentIntentId=${paymentIntentId}`,
        );

        if (!verifyResponse.ok) {
          console.error("Failed to verify payment status");
          setVerifying(false);
          return;
        }

        const verifyData = await verifyResponse.json();

        // If payment succeeded but wasn't processed yet, process it now
        if (verifyData.shouldProcess && verifyData.status === "succeeded") {
          console.log(
            "Payment verified but not processed. Processing now...",
            paymentIntentId,
          );

          // Process payment (backup if frontend callback failed)
          const processResponse = await fetch(
            `${BACKEND_BASE_URL}/api/gift-cards/holiday/payment-success`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                paymentIntentId,
                email: paymentData?.email,
                fullName: paymentData?.fullName,
                phone: paymentData?.phone,
                amount: 89,
                variant: paymentData?.variant,
              }),
            },
          );

          if (processResponse.ok) {
            console.log(
              "✅ Payment processed successfully from thank you page",
            );
            setPaymentVerified(true);
          } else {
            console.error("Failed to process payment from thank you page");
          }
        } else if (verifyData.processed) {
          console.log("Payment already processed");
          setPaymentVerified(true);
        } else if (verifyData.status === "succeeded") {
          setPaymentVerified(true);
        }
      } catch (error) {
        console.error("Error verifying payment:", error);
        // Continue showing thank you page even if verification fails
      } finally {
        setVerifying(false);
      }
    };

    verifyAndProcessPayment();
  }, [paymentIntentId, paymentData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50">
      <NavbarDemo />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        {verifying && (
          <div className="text-center py-12">
            <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
            <p className="text-gray-600">Verifying your payment...</p>
          </div>
        )}

        {!verifying && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center">
            {/* Success Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
              <CheckCircle className="w-16 h-16 text-white" />
            </motion.div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              You're All Set — Your Gift Card Is On the Way! 🎁
            </h1>

            {/* Subheadline */}
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Thank you for your purchase! Your personalized $120 massage gift
              card will arrive in your email within 2 hours.
            </p>

            {/* Order Details Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-xl p-6 md:p-8 mb-8 max-w-xl mx-auto">
              <div className="text-left space-y-4">
                <div className="flex justify-between items-center border-b pb-4">
                  <span className="text-gray-600">Gift Card Value</span>
                  <span className="text-2xl font-bold text-gray-900">$120</span>
                </div>
                <div className="flex justify-between items-center border-b pb-4">
                  <span className="text-gray-600">Amount Paid</span>
                  <span className="text-xl font-bold text-green-600">$89</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Savings</span>
                  <span className="text-xl font-bold text-green-600">$31</span>
                </div>
              </div>

              {paymentData.email && (
                <div className="mt-6 pt-6 border-t">
                  <p className="text-sm text-gray-600 text-center">
                    Confirmation sent to: <strong>{paymentData.email}</strong>
                  </p>
                </div>
              )}
            </motion.div>

            {/* What Happens Next */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 md:p-8 mb-8 max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                What Happens Next?
              </h2>
              <div className="space-y-4 text-left">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Confirmation Email
                    </h3>
                    <p className="text-gray-600">
                      You'll receive a confirmation email within minutes.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Personalized Gift Card
                    </h3>
                    <p className="text-gray-600">
                      Your personalized gift card PDF will be emailed within 2
                      hours. Reply to the email if you want a specific name
                      printed.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">
                      Share & Enjoy
                    </h3>
                    <p className="text-gray-600">
                      Share your gift card with a loved one or use it yourself!
                      Valid for 1 year from purchase.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center max-w-xl mx-auto">
              <Button
                onClick={() => navigate("/gift-cards/christmas")}
                size="lg"
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white text-lg px-8 py-6 h-auto shadow-lg hover:shadow-xl transition-all duration-300">
                <Gift className="w-5 h-5 mr-2" />
                Buy Another Gift Card
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button
                onClick={() => navigate("/")}
                size="lg"
                variant="outline"
                className="border-2 border-gray-300 hover:border-green-500 hover:bg-green-50 text-lg px-8 py-6 h-auto transition-all duration-300">
                <Calendar className="w-5 h-5 mr-2" />
                Book a Massage
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>

            {/* Support */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-12 text-center">
              <p className="text-gray-600 mb-2">
                Need help? Contact us at{" "}
                <a
                  href="mailto:info@bookatherapy.com"
                  className="text-green-600 hover:text-green-700 font-semibold">
                  info@bookatherapy.com
                </a>
              </p>
              <p className="text-sm text-gray-500">
                Your gift card is valid for 1 year from purchase date.
              </p>
            </motion.div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

